import { AnimatePresence, motion } from "motion/react";
import type React from "react";
import { cloneElement } from "react";
import { Link } from "react-router";
import { twJoin } from "tailwind-merge";

import { cardSurfaceVariants } from "@/design-system/Card";
import { Info } from "@/design-system/Info";
import { Inline } from "@/design-system/Inline";
import { Spread } from "@/design-system/Spread";

import type { IconComponent } from "../Icon/Icon";

interface AnalyticsWidgetCardProps {
  title?: string;
  titleTooltip?: string;
  helperText?: string;
  icon?: IconComponent;
  children?: React.ReactNode;
  href?: string;
  id?: string;
  renderBadge?: () => React.ReactNode;
  renderAction?: () => React.ReactNode;
  renderSkeleton?: () => React.ReactNode;
  renderEmptyState?: () => React.ReactNode;
  isLoading?: boolean;
  isDataAvailable?: boolean;
}

export function AnalyticsWidgetCard({
  title,
  titleTooltip,
  icon: Icon,
  helperText,
  children,
  href,
  id,
  isLoading,
  isDataAvailable,
  renderBadge,
  renderAction,
  renderSkeleton,
  renderEmptyState,
}: AnalyticsWidgetCardProps): React.ReactNode {
  const ContentComponent = (
    <article
      id={id}
      className={twJoin(
        "group/container flex w-full flex-col items-center gap-space-lg overflow-hidden",
        cardSurfaceVariants({ interactive: Boolean(href) }),
      )}
    >
      <section className="flex w-full flex-1 flex-col gap-space-md p-space-md @xl:p-space-lg">
        {/* Header */}
        {(title || Icon) && (
          <Spread space="md" align="start">
            {title && (
              <Inline gap="sm" align="center" wrap={false}>
                {titleTooltip && <Info tooltip={titleTooltip} />}
                <h3 className="line-clamp-2 text-heading-3">{title}</h3>
              </Inline>
            )}
            {Icon && (
              <span className="rounded-full bg-background-secondary p-space-sm">
                {cloneElement(Icon, { tone: "on-default-secondary" })}
              </span>
            )}
          </Spread>
        )}
        <section className="relative flex w-full flex-1 flex-col gap-space-md">
          <AnimatePresence>
            {((isLoading && renderSkeleton) || (!isDataAvailable && renderEmptyState)) && (
              <motion.div
                initial={{ opacity: 1 }}
                exit={{
                  opacity: 0,
                  transition: { duration: 0.2, delay: 0.4 },
                }}
                className="absolute inset-0 z-20 flex w-full items-start justify-center bg-background-default"
              >
                {isLoading && renderSkeleton && renderSkeleton()}
                {!isLoading && !isDataAvailable && renderEmptyState && renderEmptyState()}
              </motion.div>
            )}
          </AnimatePresence>
          {/* Body */}
          <section className="relative flex flex-1 flex-col gap-space-md">{children}</section>
          {/* Footer */}
          {(helperText || renderBadge) && (
            <footer className="relative flex items-end justify-between gap-space-md">
              {helperText && <p className="text-caption text-text-secondary">{helperText}</p>}
              {renderBadge && <span className="ml-auto inline-flex">{renderBadge()}</span>}
            </footer>
          )}
        </section>
      </section>
      {renderAction && (
        <footer className="relative flex items-center justify-end gap-space-sm border-t border-border-default p-space-md @xl:px-space-lg">
          {renderAction()}
        </footer>
      )}
    </article>
  );

  if (href) {
    return <Link to={href}>{ContentComponent}</Link>;
  }

  return ContentComponent;
}
